/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Button,
    Wrap,
    WrapItem,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CustomCloseWhite} from '../../../components/icons';
import {REMOVE_FILTER} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const SelectedRefinements = ({toggleFilter, selectedFilterValues = {}, urlRefine, filters, handleReset, ...props}) => {
    const styles = useMultiStyleConfig('PageFilters')
    const {formatMessage, formatNumber} = useIntl()
    const {currency} = useCurrency()

    let selectedFilters = []
    const filterByUrlRefine =
        urlRefine && typeof urlRefine === 'object' && !Array.isArray(urlRefine)

    // Display chips in the order the user selected them.
    // SFCC's `selectedRefinements` is returned in a canonical (typically alphabetical)
    // order, so it loses selection order. The URL preserves it — `use-refinements.js`
    // appends new values within an attribute via Array.push, and new attribute keys are
    // added last on the `refine` object. Iterating `urlRefine` therefore reflects the
    // order in which filters were applied.
    const orderedKeys = filterByUrlRefine
        ? [
              // Keys present in the URL (in URL/selection order)
              ...Object.keys(urlRefine).filter(
                  (k) => selectedFilterValues[k] != null
              ),
              // Keys only present in the API response (auto-applied, e.g. from
              // suggested-phrase rules) appended at the end. These are filtered
              // out below by the same check as before, but included here for
              // parity with the previous behaviour if `urlRefine` is absent.
              ...Object.keys(selectedFilterValues).filter(
                  (k) => !Object.prototype.hasOwnProperty.call(urlRefine, k)
              )
          ]
        : Object.keys(selectedFilterValues)

    // Resolves the ordered value list for an attribute. Prefers the URL value
    // (user selection order) when available; falls back to the API value.
    const getOrderedValues = (key) => {
        const raw = filterByUrlRefine && urlRefine[key] != null
            ? urlRefine[key]
            : selectedFilterValues[key]
        if (raw == null) return []
        if (Array.isArray(raw)) return raw.map((v) => String(v))
        if (typeof raw === 'number') return [String(raw)]
        return String(raw).split('|').filter((v) => v.length > 0)
    }

    for (const key of orderedKeys) {
        // Only render chips for refinements the user actually applied via the URL.
        // Auto-applied refinements (e.g. from SFCC suggested-phrase rules) live in the
        // API selectedRefinements but not in the URL, and can't be removed client-side.
        if (filterByUrlRefine && !Object.prototype.hasOwnProperty.call(urlRefine, key)) continue
        const filters = getOrderedValues(key)
        filters?.forEach((filter) => {
            let uiLabel = filter

            if (key === 'price') {
                // Parse price range string like (1..41)
                const match = filter.match(/\((\d+(?:\.\d+)?)\.\.(\d+(?:\.\d+)?)\)/)
                if (match) {
                    const min = parseFloat(match[1])
                    const max = parseFloat(match[2])

                    // Format min and max as currency without decimals
                    const formattedMin = formatNumber(min, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0
                    })
                    const formattedMax = formatNumber(max, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0
                    })

                    uiLabel = `${formattedMin} - ${formattedMax}`
                }
            }

            const selected = {
                uiLabel,
                value: key,
                apiLabel: filter
            }

            if (selected.value !== 'htype' && selected.value !== 'cgid') {
                selectedFilters.push(selected)
            }
        })
    }

    if (selectedFilters?.length === 0) {
        return null
    }

    return (
        <Wrap
            direction="row"
            align="center"
            display="flex"
            flexWrap="wrap"
            data-testid="sf-selected-refinements"
            spacing={props?.spacing || 1}
            {...styles.srContainer}
        >
            {selectedFilters?.map((filter, idx) => {
                return (
                    <WrapItem key={idx}>
                        <Box marginLeft={0}>
                            <Button
                                variant={'selectedFilter'}
                                iconSpacing={1}
                                rightIcon={
                                    <CustomCloseWhite {...styles.srRemove}/>
                                }
                                onClick={() =>
                                    toggleFilter({value: filter.apiLabel}, filter.value, true)
                                }
                                aria-label={formatMessage(REMOVE_FILTER, {label: filter.uiLabel})}
                            >
                                <Text as="span" {...styles.filterLabel}>
                                    {filter.uiLabel}
                                </Text>
                            </Button>
                        </Box>
                    </WrapItem>
                )
            })}

            {selectedFilters?.length > 0 && (
                <WrapItem {...styles.srClearAllWrapper}>
                    <Box>
                        <Button
                            padding={{base: 2}}
                            variant="unstyled"
                            onClick={handleReset}
                            aria-label={formatMessage({
                                id: 'selected_refinements.action.assistive_msg.clear_all',
                                defaultMessage: 'Clear all filters'
                            })}
                            {...styles.srClearAllCTA}
                        >
                            <Box as={'span'} {...styles.srClearAllCTA}>
                                <FormattedMessage
                                    defaultMessage="Clear All"
                                    id="selected_refinements.action.clear_all"
                                />
                            </Box>
                        </Button>
                    </Box>
                </WrapItem>
            )}
        </Wrap>
    )
}

SelectedRefinements.propTypes = {
    filters: PropTypes.array,
    selectedFilterValues: PropTypes.object,
    urlRefine: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    toggleFilter: PropTypes.func,
    handleReset: PropTypes.func
}

export default SelectedRefinements
